/**
 * Enum representing the different location types returned by the search bar API.
 * These values are used to filter or categorize search results based on location.
 * Use these constants when processing the location type from an API response.
 * This enum is for 2.0 mapping only.
 */
export default class CompletionSuggestionType {
  static get County() {
    return this.getInstance('CountyEquivalent');
  }

  /**
   * Gets the Neighborhood location type.
   * @returns {CompletionSuggestionType} The Neighborhood location type.
   */
  static get Neighborhood() {
    return this.getInstance('Neighborhood');
  }

  /**
   * Gets the Zip location type.
   * @returns {CompletionSuggestionType} The Zip location type.
   */
  static get Zip() {
    return this.getInstance('PostalCode');
  }

  /**
   * Gets the City location type.
   * @returns {CompletionSuggestionType} The City location type.
   */
  static get City() {
    return this.getInstance('City');
  }

  /**
   * Gets the School District location type.
   * @returns {CompletionSuggestionType} The School District location type.
   */
  static get SchoolDistrict() {
    return this.getInstance('SchoolDistrict');
  }

  /**
   * Gets the Market location type.
   * @returns {CompletionSuggestionType} The Market location type.
   */
  static get Market() {
    return this.getInstance('MarketAreaPopularlyNamedRegion');
  }

  /**
   * Gets the Article result category type.
   * @returns {CompletionSuggestionType} The Article result category type.
   */
  static get Article() {
    return this.getInstance('Article');
  }

  /**
   * Gets the State location type.
   * @returns {CompletionSuggestionType} The State location type.
   */
  static get State() {
    return this.getInstance('StateEquivalent');
  }

  constructor(name) {
    this.name = name;
  }

  /**
   * A static object to hold instances of CompletionSuggestionType.
   * This ensures that each location type is a singleton.
   */
  static fromKey(key) {
    const locationMap = {
      county: CompletionSuggestionType.County,
      neighborhood: CompletionSuggestionType.Neighborhood,
      zip: CompletionSuggestionType.Zip,
      city: CompletionSuggestionType.City,
      schooldistrict: CompletionSuggestionType.SchoolDistrict,
      market: CompletionSuggestionType.Market
    };
    return locationMap[key];
  }

  /**
   * Gets an instance of CompletionSuggestionType based on the provided key.
   * If an instance with the same key already exists, it returns that instance.
   * Otherwise, it creates a new instance and stores it for future use.
   * @param {string} key - The key representing the location type.
   * @returns {CompletionSuggestionType} An instance of CompletionSuggestionType.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new CompletionSuggestionType(key);
    }
    return this.instances[key];
  }

  /**
   * Converts the location type to a string representation.
   * @returns {string} The string representation of the location type.
   */
  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(CompletionSuggestionType, 'instances', { value: {} });